import type { Corridor, DeployedUnit } from "../../api/types";
import { unitDesignation } from "../../i18n/designation";
import { lang, t } from "../../i18n/i18n";

/** Bulle d'une unité au survol et mesure distance-temps : calculs de l'iPad (`ChatController.hoverInfo`,
 *  `MapLibreMapView` : formats, allure par bande de LIMA, arrondi au quart d'heure). */

export type Tone = "ok" | "warn" | "brand";

export interface UnitInfo {
  title: string;
  materiel: string | null;
  quantity: string | null;
  potential: { text: string; tone: Tone };
  /** Portée de l'arme principale (m) et texte (« 3 km · canon 120 mm ») ; null : inconnue ou non armé. */
  range: { m: number; text: string } | null;
  speedKmh: number | null;
  aerial: boolean;
  side: DeployedUnit["side"];
}

/** Titre sans les codes de nation entre parenthèses (« 111(TI)SONRECO » → « 111SONRECO »), comme l'iPad ; en
 *  anglais, désignation OTAN (« 111 Recce Pl »). */
export function bubbleTitle(unit: DeployedUnit): string {
  const name = unitDesignation(unit.display_name ?? unit.designation ?? unit.code);
  return name.replace(/\([A-Z]{2,3}\)/g, "").replace(/\s{2,}/g, " ").trim();
}

/** Nombre à une décimale, virgule décimale en français (« 1,5 »), point en anglais (« 1.5 »). */
function oneDecimal(value: number): string {
  const text = value.toFixed(1);
  return lang() === "en" ? text : text.replace(".", ",");
}

/** « Renforcé » au-delà de 100 %, sinon « 80 % » ; rouge jusqu'à 50 %, orange jusqu'à 90 %. */
export function potentialLabel(fraction: number): { text: string; tone: Tone } {
  const text =
    fraction > 1.0001
      ? t("Renforcé")
      : fraction >= 0.9999
        ? t("{n} %", { n: 100 })
        : t("{n} %", { n: Math.round(fraction * 100) });
  return { text, tone: fraction <= 0.5 ? "brand" : fraction <= 0.9 ? "warn" : "ok" };
}

/** Portée : « 850 m », « 3 km », « 1,5 km » (mètres tronqués, comme l'iPad). */
export function formatRange(m: number): string {
  if (m < 1000) return `${Math.trunc(m)} m`;
  const km = m / 1000;
  return Number.isInteger(km) ? `${km} km` : `${oneDecimal(km)} km`;
}

/** Distance mesurée : « 850 m », « 12,3 km ». */
export function formatDistance(m: number): string {
  return m < 1000 ? `${Math.round(m)} m` : `${oneDecimal(m / 1000)} km`;
}

/** Durée arrondie au quart d'heure supérieur (15 min au moins) : « 45 mn », « 1 h », « 1 h 15 » (en anglais
 *  « 45 min », « 1 h », « 1 h 15 min »). */
export function formatDuration(hours: number): string {
  let minutes = Math.ceil((hours * 60) / 15 - 1e-9) * 15;
  if (minutes < 15) minutes = 15;
  if (minutes < 60) return t("{n} mn", { n: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : t("{h} h {m}", { h, m: String(m).padStart(2, "0") });
}

export function unitInfo(unit: DeployedUnit, strength: number | undefined): UnitInfo {
  const fraction = strength ?? 1;
  const known = unit.materiel.filter((m) => m.known);
  const armed = known.find((m) => (m.range_m ?? 0) > 0);
  const weapon = armed?.category?.includes("—") ? armed.category.split("—").slice(1).join("—").trim() : "";
  const moving = known.find((m) => (m.speed_kmh ?? 0) > 0);
  const quantity =
    unit.quantity !== null && unit.quantity !== undefined ? String(Math.round(unit.quantity * fraction)) : null;
  return {
    title: bubbleTitle(unit),
    materiel: unit.materiel.length ? unit.materiel.map((m) => m.name).join(", ") : null,
    quantity,
    potential: potentialLabel(fraction),
    range: armed?.range_m
      ? { m: armed.range_m, text: weapon ? `${formatRange(armed.range_m)} · ${weapon}` : formatRange(armed.range_m) }
      : null,
    speedKmh: moving?.speed_kmh ?? null,
    aerial: Boolean(moving?.milieu && /^a[ée]r/i.test(moving.milieu)),
    side: unit.side,
  };
}

// --- Mesure distance et temps ------------------------------------------------------------------------------

type LonLat = [number, number];
const M_PER_DEG = 111_320;

/** Distance à vol d'oiseau (m), sur la sphère (comme le serveur). */
export function distanceM(a: LonLat, b: LonLat): number {
  const r = 6_371_000;
  const la1 = (a[1] * Math.PI) / 180;
  const la2 = (b[1] * Math.PI) / 180;
  const dla = la2 - la1;
  const dlo = ((b[0] - a[0]) * Math.PI) / 180;
  const h = Math.sin(dla / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dlo / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Part du trajet avant L2 (allure de reconnaissance de L0 à L2, plus soutenue au-delà) : L2 vue comme la droite
 *  passant par son centre, perpendiculaire à l'axe de progression (du centre de la première ligne à celui de la
 *  dernière), comme l'iPad ; sans L2 ou sans axe, tout le trajet à l'allure de reconnaissance. */
export function fractionBeforeL2(corridor: Corridor | null | undefined, from: LonLat, to: LonLat): number {
  const lines = corridor?.lines ?? [];
  const ordered = ["L0", "L1", "L2", "L3"]
    .map((name) => lines.find((l) => l.name === name))
    .filter((l): l is NonNullable<typeof l> => Boolean(l));
  const l2 = lines.find((l) => l.name === "L2");
  if (!l2 || ordered.length < 2) return 1;
  const cos = Math.cos((from[1] * Math.PI) / 180);
  const xy = (p: LonLat): [number, number] => [p[0] * M_PER_DEG * cos, p[1] * M_PER_DEG];
  const first = xy(ordered[0]!.center);
  const last = xy(ordered[ordered.length - 1]!.center);
  const len = Math.hypot(last[0] - first[0], last[1] - first[1]);
  if (len === 0) return 1;
  const axis = [(last[0] - first[0]) / len, (last[1] - first[1]) / len];
  const c2 = xy(l2.center);
  const f = xy(from);
  const t = xy(to);
  const pFrom = (f[0] - c2[0]) * axis[0]! + (f[1] - c2[1]) * axis[1]!;
  const pTo = (t[0] - c2[0]) * axis[0]! + (t[1] - c2[1]) * axis[1]!;
  if (pFrom <= 0 && pTo <= 0) return 1;
  if (pFrom >= 0 && pTo >= 0) return 0;
  const cross = pFrom / (pFrom - pTo);
  return pFrom < 0 ? cross : 1 - cross;
}

/** Temps de trajet (h) : vitesse du matériel ÷ 3 avant L2, ÷ 2,5 au-delà, × coefficient de mobilité (1 pour un
 *  aéronef) ; null sans vitesse connue. */
export function travelHours(km: number, speedKmh: number | null, coefficient: number, fractionRecce: number): number | null {
  if (!speedKmh || speedKmh <= 0 || coefficient <= 0) return null;
  const recce = (speedKmh / 3) * coefficient;
  const assault = (speedKmh / 2.5) * coefficient;
  return (km * fractionRecce) / recce + (km * (1 - fractionRecce)) / assault;
}

/** Cinq points régulièrement répartis du départ à l'arrivée (moyenne du coefficient le long du trajet). */
export function pathSamples(from: LonLat, to: LonLat, count = 5): LonLat[] {
  return Array.from({ length: count }, (_, i) => {
    const t = count === 1 ? 0.5 : i / (count - 1);
    return [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t] as LonLat;
  });
}

// --- Placement à l'écran (bulle, étiquette de portée, pastilles de mesure) ---------------------------------

export interface Pt {
  x: number;
  y: number;
}
export interface Size {
  w: number;
  h: number;
}

/** Hauteur de la pointe de la bulle. */
export const CARET = 7;
const MARGIN = 8;

/** Bulle d'une unité : coin haut-gauche de la carte (pointe exclue), côté et abscisse de la pointe. Au-dessus de
 *  l'unité de préférence, en dessous faute de place, jamais coupée ; pendant une mesure, du côté opposé au
 *  pointeur quand il y a la place, pour dégager la flèche (comme l'iPad). La pointe reste sur l'unité sans mordre
 *  les coins arrondis. */
export function placeBubble(anchor: Pt, card: Size, frame: Size, clearance: number, preferAbove?: boolean) {
  const total = card.h + CARET;
  const spaceAbove = anchor.y - clearance - MARGIN;
  const spaceBelow = frame.h - MARGIN - (anchor.y + clearance);
  const auto = spaceAbove >= total ? true : spaceBelow >= total ? false : spaceAbove >= spaceBelow;
  const above =
    preferAbove === undefined ? auto : (preferAbove ? spaceAbove : spaceBelow) >= total ? preferAbove : auto;
  const low = above ? MARGIN : MARGIN + CARET;
  const high = above ? frame.h - MARGIN - total : frame.h - MARGIN - card.h;
  const y = Math.min(Math.max(above ? anchor.y - clearance - total : anchor.y + clearance + CARET, low), Math.max(low, high));
  const x = Math.min(Math.max(anchor.x - card.w / 2, MARGIN), Math.max(MARGIN, frame.w - MARGIN - card.w));
  const lo = 12 + CARET;
  const hi = card.w - 12 - CARET;
  const caretX = Math.min(Math.max(anchor.x - x, Math.min(lo, hi)), Math.max(lo, hi));
  return { x, y, above, caretX };
}

/** Centre d'une étiquette ramené dans la carte, à `margin` px des bords. */
export function clampCenter(c: Pt, size: Size, frame: Size, margin = 6): Pt {
  const hw = size.w / 2;
  const hh = size.h / 2;
  return {
    x: Math.min(Math.max(c.x, hw + margin), Math.max(hw + margin, frame.w - hw - margin)),
    y: Math.min(Math.max(c.y, hh + margin), Math.max(hh + margin, frame.h - hh - margin)),
  };
}

/** « Portée x » sur le bord de l'anneau opposé à la bulle ; de l'autre côté s'il la chevauche encore. */
export function ringLabelCenter(
  center: Pt,
  radius: number,
  label: Size,
  frame: Size,
  bubble: { x: number; y: number; w: number; h: number } | null,
): Pt {
  const bubbleAbove = bubble ? bubble.y + bubble.h / 2 <= center.y : true;
  const at = (y: number) => clampCenter({ x: center.x, y }, label, frame);
  const overlaps = (c: Pt) =>
    bubble !== null &&
    c.x - label.w / 2 < bubble.x + bubble.w &&
    c.x + label.w / 2 > bubble.x &&
    c.y - label.h / 2 < bubble.y + bubble.h &&
    c.y + label.h / 2 > bubble.y;
  const first = at(bubbleAbove ? center.y + radius : center.y - radius);
  return overlaps(first) ? at(bubbleAbove ? center.y - radius : center.y + radius) : first;
}

/** Flèche de mesure : trait (du bord du symbole à la base de la pointe), pointe de 13 px sur 13, milieu et
 *  normale (pastille décalée de 16 px du trait) ; null à moins de 8 px de l'unité (rien à mesurer). */
export function arrowGeometry(from: Pt, to: Pt, startGap = 0) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len <= 8) return null;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const head = 13;
  const half = 6.5;
  const end = { x: to.x - ux * head, y: to.y - uy * head };
  const gap = Math.min(startGap, Math.max(0, len - head - 6));
  return {
    start: { x: from.x + ux * gap, y: from.y + uy * gap },
    end,
    head: [to, { x: end.x + px * half, y: end.y + py * half }, { x: end.x - px * half, y: end.y - py * half }] as Pt[],
    label: { x: (from.x + to.x) / 2 + px * 16, y: (from.y + to.y) / 2 + py * 16 },
  };
}

/** Moyenne des coefficients échantillonnés le long du trajet (temps) et coefficient au point visé (puce).
 *  `fair` : les mêmes points par beau temps (`fair_coefficients` de la route Mobilité), moyenne rendue dans `fair`
 *  (null sans eux, serveur d'avant ou nombre de points différent). */
export function pathCoefficients(coefficients: number[], fair?: number[]): { path: number; end: number; fair: number | null } | null {
  if (!coefficients.length) return null;
  const mean = (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length;
  const fairPath = fair && fair.length === coefficients.length ? mean(fair) : null;
  return { path: mean(coefficients), end: coefficients[coefficients.length - 1]!, fair: fairPath };
}

/** Temps de trajet avec la météo comptée une seule fois, comme les ordres et la logistique : terrain par beau
 *  temps majoré de la ligne « Délais » (`delayPct`) ; sans coefficient de beau temps, celui du jour. */
export function weatherTravelHours(
  km: number,
  speedKmh: number | null,
  mobility: { path: number; fair: number | null },
  delayPct: number,
  fractionRecce: number,
): number | null {
  if (mobility.fair === null) return travelHours(km, speedKmh, mobility.path, fractionRecce);
  const hours = travelHours(km, speedKmh, mobility.fair, fractionRecce);
  return hours === null ? null : hours * (1 + Math.max(0, delayPct) / 100);
}
